import { create } from "zustand";
import type { CalendarView } from "./settings.store.js";

type Step = (delta: number) => void;
type Anchors = Partial<Record<CalendarView, string>>;

/** Lets the global keyboard shortcuts (j/k) step the calendar's current view
 *  without lifting each route's anchor into global state. The active calendar
 *  route registers a `step` callback on mount (shifting its own anchor by one
 *  unit) and clears it on unmount; non-calendar routes leave it null so j/k are
 *  inert there. */
interface CalendarNavState {
  step: Step | null;
  anchors: Anchors;
  setStep: (step: Step | null) => void;
  getAnchor: (view: CalendarView) => Date | null;
  rememberAnchor: (view: CalendarView, anchor: Date) => void;
  clearAnchors: (views: CalendarView[]) => void;
}

export const useCalendarNavStore = create<CalendarNavState>((set, get) => ({
  step: null,
  anchors: {},
  setStep: (step) => set({ step }),
  getAnchor: (view) => {
    const iso = get().anchors[view];
    if (!iso) return null;
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? null : date;
  },
  rememberAnchor: (view, anchor) => set((state) => {
    const iso = anchor.toISOString();
    if (state.anchors[view] === iso) return state;
    return { anchors: { ...state.anchors, [view]: iso } };
  }),
  clearAnchors: (views) => set((state) => {
    const anchors = { ...state.anchors };
    for (const view of views) delete anchors[view];
    return { anchors };
  }),
}));
